/**
 * Whether this browser uses the LifeMD login service or the portal's own sign-in.
 *
 * A known login host (`loginServiceOrigin`) makes the login service available, but a
 * browser uses it only after opting in, so testers can try it in a shared environment without
 * changing sign-in for anyone else:
 *
 * - `?login=new`        the login service, following its flags
 * - `?login=new-workos` the login service, pinned to WorkOS (the SDK's `modern: true`)
 * - `?login=old`        back to the portal's own sign-in
 *
 * The choice is a cookie on the parent domain (`.dev.lifemd.com`), so one opt-in covers the patient
 * and physician portals. The SDK is configured once, at start, so a change reloads the page.
 *
 * Kept the same in care-patient-portal-web and care-physician-portal-web.
 */

export type LoginChoice = 'new' | 'new-workos';

export const LOGIN_CHOICE_COOKIE = 'lmd_login';

const LOGIN_PARAM = 'login';
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/** What this module touches in the browser, so tests can stand in for it. */
export type LoginChoicePage = {
  cookie: () => string;
  hostname: string;
  href: () => string;
  reload: () => void;
  replaceUrl: (url: string) => void;
  setCookie: (value: string) => void;
};

const browserPage = (): LoginChoicePage => ({
  cookie: () => document.cookie,
  hostname: window.location.hostname,
  href: () => window.location.href,
  reload: () => window.location.reload(),
  replaceUrl: (url) => window.history.replaceState(window.history.state, '', url),
  setCookie: (value) => {
    document.cookie = value;
  }
});

const isLoginChoice = (value: string | null | undefined): value is LoginChoice =>
  value === 'new' || value === 'new-workos';

/** This browser's choice; null means the portal's own sign-in. */
export const readLoginChoice = (
  cookie: string = typeof document === 'undefined' ? '' : document.cookie
): LoginChoice | null => {
  const value = cookie
    .split(';')
    .map((pair) => pair.trim())
    .find((pair) => pair.startsWith(`${LOGIN_CHOICE_COOKIE}=`))
    ?.slice(LOGIN_CHOICE_COOKIE.length + 1);

  return isLoginChoice(value) ? value : null;
};

/**
 * The domain both portals of an environment share: the host without its first label
 * (`physician2.dev.lifemd.com` and `care.dev.lifemd.com` share `.dev.lifemd.com`). Undefined keeps
 * the cookie on this host: localhost, an IP, or a host too short to have a sibling.
 */
export const cookieDomainFor = (hostname: string) => {
  const labels = hostname.split('.');

  if (labels.length < 3 || labels.every((label) => /^\d+$/.test(label))) {
    return undefined;
  }

  return `.${labels.slice(1).join('.')}`;
};

const cookieValue = (value: string, maxAge: number, domain?: string) =>
  [
    `${LOGIN_CHOICE_COOKIE}=${value}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
    'Secure',
    ...(domain ? [`Domain=${domain}`] : [])
  ].join('; ');

/**
 * Stores the choice, null for the portal's own sign-in. A browser refuses a Domain cookie on a
 * public suffix (a preview channel on `web.app`), so a choice that did not stick is kept on this
 * host instead.
 */
export const writeLoginChoice = (
  choice: LoginChoice | null,
  page: LoginChoicePage = browserPage()
) => {
  const domain = cookieDomainFor(page.hostname);

  if (!choice) {
    if (domain) {
      page.setCookie(cookieValue('', 0, domain));
    }
    page.setCookie(cookieValue('', 0));
    return;
  }

  if (domain) {
    page.setCookie(cookieValue(choice, MAX_AGE_SECONDS, domain));
    if (readLoginChoice(page.cookie()) === choice) {
      return;
    }
  }
  page.setCookie(cookieValue(choice, MAX_AGE_SECONDS));
};

/**
 * Applies `?login=` from the address, once the login service (if any) is installed for the current
 * choice. The parameter is removed from the address either way. A different choice is stored and the
 * page reloads, so the SDK starts with it; leaving the login service signs out there first, so the
 * store keeps no session the portal can no longer renew. Returns true when the page reloads.
 *
 * `available` is false where the build has no login host: the parameter is then left alone.
 */
export const applyLoginChoiceFromUrl = async (
  auth: { signOut: () => Promise<void> } | null,
  available: boolean,
  page: LoginChoicePage = browserPage()
): Promise<boolean> => {
  if (!available) {
    return false;
  }

  const url = new URL(page.href());
  const requested = url.searchParams.get(LOGIN_PARAM);

  if (requested !== 'old' && !isLoginChoice(requested)) {
    return false;
  }

  url.searchParams.delete(LOGIN_PARAM);
  page.replaceUrl(`${url.pathname}${url.search}${url.hash}`);

  const next = requested === 'old' ? null : requested;

  if (next === readLoginChoice(page.cookie())) {
    return false;
  }

  if (!next && auth) {
    await auth.signOut().catch(() => undefined);
  }

  writeLoginChoice(next, page);
  page.reload();
  return true;
};
